import _ from 'underscore';
import * as putil from '@bingads-webui-universal/primitive-utilities';
import { Base } from './base';
import { IValidationRule } from '../types';

/**
 * Define a BigInt validator which check whether input is a BigInt
 * @param {I18nModel} i18n - provide callback getString to get message,
 * will be called with args 'Validation_BigInt_Require_BigInt_Type'
 */
class Type extends Base implements IValidationRule {
  blockInput: boolean;

  message: string;

  constructor(i18n: I18nModel) {
    super(i18n);

    this.blockInput = false;
    this.message = i18n.getString('Validation_BigInt_Require_BigInt_Type');
  }

  isValidValue(value: any): boolean {
    return putil.isNil(value) || typeof value === 'bigint';
  }
}

/**
 * Define a BigInt validator which check whether input is in a valid size
 * @param {int} integerLength - the length of int
 * @param {int} decimalLength - the length of decimal
 * @param {I18nModel} i18n - provide callback getString to get message,
 * will be called with args 'Validation_BigInt_Max_Length'
 */
export class Size extends Base implements IValidationRule {
  blockInput: boolean;

  decimalLength: number;

  integerLength: number;

  message: string;

  constructor(integerLength: number, decimalLength: number, i18n: I18nModel) {
    super(i18n);

    this.blockInput = false;
    this.message = i18n.getString('Validation_BigInt_Max_Length');
    this.integerLength = integerLength;
    this.decimalLength = decimalLength;
  }

  isValidValue(value: any): boolean {
    // leave for required validator
    if (putil.isNil(value)) {
      return true;
    }

    // leave for big int validator
    if (typeof value !== 'bigint') {
      return true;
    }

    let text = value.toString();

    if (value < 0) {
      text = text.substr(1);
    }

    const segments = text.split('.');

    if (this.integerLength && _.size(segments[0]) > this.integerLength) {
      return false;
    }

    if (this.decimalLength !== 0 || _.size(segments) > 1) {
      return false;
    }

    return true;
  }
}

/**
 * Define a bigint validate which check whether input value in a valid range
 * @param {int|float} min - min value of input
 * @param {int|float} max - max value of input
 * @param {I18nModel} i18n - provide callback getString to get error message, will be called with args
 * 'Validation_BigInt_Range_Between', 'Validation_BigInt_Range_Min' or 'Validation_BigInt_Range_Max'
 */
export class Range extends Base implements IValidationRule {
  max: number;

  message: string;

  min: number;

  constructor(min: number, max: number, i18n: I18nModel) {
    super(i18n);

    this.min = min;
    this.max = max;

    if (_.isFinite(min) && _.isFinite(max)) {
      this.message = i18n.getString('Validation_BigInt_Range_Between', {
        min: i18n.formatDecimal(min, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
        max: i18n.formatDecimal(max, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
      });
    } else if (_.isFinite(min)) {
      this.message = i18n.getString('Validation_BigInt_Range_Min', {
        min: i18n.formatDecimal(min, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
      });
    } else {
      this.message = i18n.getString('Validation_BigInt_Range_Max', {
        max: i18n.formatDecimal(max, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
      });
    }
  }

  isValidValue(value: any): boolean {
    // leave for required validator
    if (putil.isNil(value)) {
      return true;
    }

    // leave for big int validator
    if (typeof value !== 'bigint') {
      return true;
    }

    let result = true;

    if (_.isFinite(this.min)) {
      result = result && value >= this.min;
    }

    if (_.isFinite(this.max)) {
      result = result && value <= this.max;
    }

    return result;
  }
}

/**
 * Create a BigInt type validator
 * @param {I18nModel} i18n - provide callback getString to get message
 * @returns {IValidationRule} - BigInt type validator instance
 */
export function type(i18n: I18nModel): IValidationRule {
  return new Type(i18n);
}

/**
 * Create a size validator
 * @param {int} integerLength - the length of int
 * @param {int} decimalLength - the length of decimal
 * @param {I18nModel} i18n - provide callback getString to get message
 * @returns {IValidationRule} - BigInt size validator instance
 */
export function size(integerLength: number, decimalLength: number, i18n: I18nModel): IValidationRule {
  return new Size(integerLength, decimalLength, i18n);
}

/**
 * Create a range validator
 * @param {int|float} min - min value of input
 * @param {int|float} max - max value of input
 * @param {I18nModel} i18n - provide callback getString to get message
 * @returns {IValidationRule} - BigInt range validator instance
 */
export function range(min: number, max: number, i18n: I18nModel): IValidationRule {
  return new Range(min, max, i18n);
}
